import { useState } from 'react';
import type { FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/dagob39tto0ceqqni1p0';
const WHATSAPP = 'https://wa.me/917306725502';
const EMAIL = 'mailto:info@globesynctravel.com';
const PHONE = '+91 7306725502';

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // Honeypot: silently drop bots without alerting them
    const honeypot = String(data.get('website_alt') ?? '').trim();
    if (honeypot !== '') {
      setStatus('success');
      setFormSuccess("Thanks! Your request has been received. We'll be in touch shortly.");
      form.reset();
      return;
    }

    // Build payload from live DOM form (handles browser autofill)
    const payload = new FormData();
    const name = String(data.get('name') ?? '').trim();
    const contact = String(data.get('contact') ?? '').trim();
    const service = String(data.get('service') ?? '').trim();
    const travelDates = String(data.get('travel_dates') ?? '').trim();
    if (name) payload.append('name', name);
    if (contact) payload.append('contact', contact);
    if (service) payload.append('service', service);
    if (travelDates) payload.append('travel_dates', travelDates);

    setStatus('loading');
    setFormError('');
    setFormSuccess('');

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });

      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }

      const meta = (parsed?.meta ?? {}) as Record<string, unknown>;
      const serverMsg =
        typeof meta?.message === 'string' && meta.message
          ? meta.message
          : typeof parsed?.message === 'string' && parsed.message
            ? parsed.message
            : typeof meta?.detail === 'string' && meta.detail
              ? meta.detail
              : responseText;

      const code = typeof parsed?.code === 'string' ? parsed.code : '';
      const failed =
        !res.ok ||
        code !== 'OK' ||
        (typeof serverMsg === 'string' && /spam/i.test(serverMsg));

      if (failed) {
        setStatus('error');
        setFormError(
          typeof serverMsg === 'string' && serverMsg
            ? serverMsg
            : 'Something went wrong. Please try again in a moment.'
        );
        return;
      }

      setStatus('success');
      setFormSuccess("Thanks! Your free quote request has been received. Our team will reach out within 24 hours.");
      form.reset();
    } catch {
      setStatus('error');
      setFormError('We could not reach the server. Please try again or message us on WhatsApp.');
    }
  };

  const inputClass =
    'w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder-foreground-400 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200';

  return (
    <section className="relative w-full bg-background-100 py-20 md:py-28" id="contact">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-primary-600">
            <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
            Get in touch
          </span>
          <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-foreground-950 md:text-5xl">
            Connecting You to the World, <span className="text-primary-600">Seamlessly.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-foreground-600">
            Serving clients globally from our Kakkanad, Ernakulam hub, whether you are
            managing a midnight flight change or planning a vacation, our team is
            always ready to help.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-6 md:mt-16 lg:grid-cols-2">
          {/* Left: contact methods */}
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 rounded-2xl border border-background-200 bg-background-50 p-6 md:p-8">
              <h3 className="font-heading text-xl font-bold text-foreground-950">Reach us instantly</h3>

              <a
                href={WHATSAPP}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-4 rounded-xl border border-background-200 p-4 transition-colors hover:border-primary-300"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent-500 text-background-50">
                  <i className="ri-whatsapp-line text-xl" />
                </span>
                <span className="flex-1">
                  <span className="block text-sm text-foreground-500">WhatsApp</span>
                  <span className="block font-heading text-base font-semibold text-foreground-900">{PHONE}</span>
                </span>
                <i className="ri-external-link-line text-foreground-400 transition-transform group-hover:translate-x-0.5" />
              </a>

              <a
                href={EMAIL}
                className="group flex items-center gap-4 rounded-xl border border-background-200 p-4 transition-colors hover:border-primary-300"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500 text-background-50">
                  <i className="ri-mail-line text-xl" />
                </span>
                <span className="flex-1">
                  <span className="block text-sm text-foreground-500">Email</span>
                  <span className="block truncate font-heading text-base font-semibold text-foreground-900">
                    info@globesynctravel.com
                  </span>
                </span>
                <i className="ri-arrow-right-up-line text-foreground-400 transition-transform group-hover:translate-x-0.5" />
              </a>

              <div className="flex items-start gap-4 rounded-xl border border-background-200 p-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-secondary-500 text-background-50">
                  <i className="ri-map-pin-line text-xl" />
                </span>
                <div>
                  <span className="block text-sm text-foreground-500">Headquarters</span>
                  <span className="block text-sm leading-relaxed text-foreground-800">
                    Suite No: 769, 2nd Floor, KC Arcade, Near TV Center, CSEZ P O,
                    Kakkanad, Ernakulam – 682037
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-4 rounded-xl border border-accent-200 bg-accent-50 p-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent-500 text-background-50">
                  <i className="ri-time-line text-xl" />
                </span>
                <div>
                  <span className="block text-sm text-accent-700">Business Hours</span>
                  <span className="block font-semibold text-accent-900">
                    We don&apos;t have holidays.
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right: consultation form */}
          <div className="rounded-2xl border border-background-200 bg-background-50 p-6 md:p-8">
            <h3 className="font-heading text-xl font-bold text-foreground-950">Request a Free Quote</h3>
            <p className="mt-1 text-sm text-foreground-600">
              Tell us a little about your trip and we’ll respond within 24 hours.
            </p>

            <form id="consultation-form" data-readdy-form="" onSubmit={handleSubmit} className="mt-6 space-y-4">
              {/* honeypot */}
              <div className="honey-field" aria-hidden="true">
                <label htmlFor="website_alt">Website</label>
                <input
                  id="website_alt"
                  name="website_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honey-field"
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-700">
                    Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="Your full name"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="contact" className="mb-1.5 block text-sm font-medium text-foreground-700">
                    Contact Number
                  </label>
                  <input
                    id="contact"
                    name="contact"
                    type="tel"
                    required
                    placeholder="+91 98XXXXXX00"
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="service" className="mb-1.5 block text-sm font-medium text-foreground-700">
                    Service Required
                  </label>
                  <select id="service" name="service" required className={inputClass} defaultValue="Tour Packages">
                    <option value="Tour Packages">Tour Packages</option>
                    <option value="Flights">Flights</option>
                    <option value="Hotels">Hotels</option>
                    <option value="Visas">Visas</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="travel_dates" className="mb-1.5 block text-sm font-medium text-foreground-700">
                    Travel Dates
                  </label>
                  <input id="travel_dates" name="travel_dates" type="date" className={inputClass} />
                </div>
              </div>

              <button
                type="submit"
                disabled={status === 'loading'}
                className="mt-2 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3.5 text-base font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {status === 'loading' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-lg" />
                    Sending…
                  </>
                ) : (
                  <>
                    Request a Free Quote
                    <i className="ri-arrow-right-line text-lg" />
                  </>
                )}
              </button>

              {formSuccess && (
                <p className="flex items-start gap-2 rounded-lg bg-secondary-50 p-3 text-sm text-primary-800" role="status">
                  <i className="ri-checkbox-circle-line mt-0.5 text-lg" />
                  {formSuccess}
                </p>
              )}
              {formError && (
                <p className="flex items-start gap-2 rounded-lg bg-accent-50 p-3 text-sm text-accent-900" role="alert">
                  <i className="ri-error-warning-line mt-0.5 text-lg" />
                  {formError}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}